import PhotoSwipeLightbox from 'photoswipe-lightbox';

const MOBILE_QUERY = '(max-width: 767.98px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

class MasonryLightbox extends HTMLElement {
  #lightbox = null;
  #thumbButtons = [];
  #thumbs = null;
  #caption = null;
  #previousButton = null;
  #nextButton = null;
  #mobileQuery;
  #reducedMotionQuery;
  #pswp = null;
  #syncingViewport = false;
  #syncingThumb = false;
  #captionHeight = 0;
  #placingCaption = false;
  #captionFrame = 0;

  connectedCallback() {
    if (this.#lightbox) return;

    this.#mobileQuery = window.matchMedia(MOBILE_QUERY);
    this.#reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);

    const zoomIcon = this.querySelector('.masonry-lightbox__zoom-template')?.content?.firstElementChild?.cloneNode(true);
    zoomIcon?.classList.add('pswp__icn');

    this.#lightbox = new PhotoSwipeLightbox({
      gallery: this,
      children: 'a.masonry-lightbox__trigger',
      arrowPrevSVG: iconMarkup('template-icon-chevron', 'icon--left'),
      arrowNextSVG: iconMarkup('template-icon-chevron'),
      closeSVG: iconMarkup('template-icon-close'),
      zoomSVG: zoomIcon?.outerHTML || '',
      bgOpacity: 1,
      counter: false,
      loop: true,
      showHideAnimationType: 'fade',
      paddingFn: () => ({
        top: this.#topPadding(),
        bottom: this.#bottomPadding(),
        left: 0,
        right: 0,
      }),
      pswpModule: () => import('photoswipe'),
    });

    this.#lightbox.addFilter('thumbEl', (thumbEl, data) => data.element?.querySelector('img') || this.#triggerImage(data.element) || thumbEl);
    this.#lightbox.addFilter('placeholderSrc', (placeholderSrc, slide) => {
      const image = this.#triggerImage(slide.data.element);
      return image?.currentSrc || image?.src || placeholderSrc;
    });

    this.#lightbox.on('uiRegister', () => {
      this.#pswp = this.#lightbox.pswp;
      if (this.#titleUsed()) {
        this.#pswp.ui.registerElement({
          name: 'masonryCaption',
          className: 'masonry-lightbox__caption',
          appendTo: 'wrapper',
          order: 11,
          onInit: (element, pswp) => {
            this.#caption = element;
            if (this.#titleActive()) this.#syncCaption(pswp.currIndex);
            else element.hidden = true;
          },
        });
      }
      this.#pswp.ui.registerElement({
        name: 'masonryThumbnails',
        className: 'masonry-lightbox__rail',
        appendTo: 'wrapper',
        order: 12,
        onInit: (element, pswp) => this.#initRail(element, pswp),
      });
    });

    this.#lightbox.on('close', () => {
      if (this.#captionFrame) cancelAnimationFrame(this.#captionFrame);
      this.#captionFrame = 0;
      this.#pswp = null;
      this.#thumbButtons = [];
      this.#thumbs = null;
      this.#caption = null;
    });

    this.#lightbox.init();
  }

  disconnectedCallback() {
    this.#lightbox?.destroy();
    this.#lightbox = null;
    this.#pswp = null;
    this.#thumbButtons = [];
    this.#thumbs = null;
    this.#caption = null;
  }

  #initRail(element, pswp) {
    element.addEventListener('pointerdown', (event) => event.stopPropagation());
    element.addEventListener('click', (event) => event.stopPropagation());

    this.#previousButton = this.#createRailButton('previous');
    this.#thumbs = document.createElement('div');
    this.#thumbs.className = 'masonry-lightbox__thumbs';
    this.#nextButton = this.#createRailButton('next');

    const triggers = Array.from(this.querySelectorAll('a.masonry-lightbox__trigger'));
    this.#thumbButtons = triggers.map((trigger, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'masonry-lightbox__thumb';
      button.dataset.thumbSrc = trigger.dataset.thumbSrc || '';
      button.dataset.thumbSrcMobile = trigger.dataset.thumbSrcMobile || trigger.dataset.thumbSrc || '';
      button.setAttribute('aria-label', trigger.getAttribute('aria-label') || trigger.dataset.thumbAlt || '');

      const image = document.createElement('img');
      image.alt = trigger.dataset.thumbAlt || '';
      image.width = this.#thumbSize();
      image.height = this.#thumbBoxHeight(trigger);
      button.style.aspectRatio = this.#thumbAspectRatio(trigger);
      this.#prepareThumb(image, index, pswp.currIndex);
      image.src = this.#mobileQuery.matches ? button.dataset.thumbSrcMobile : button.dataset.thumbSrc;
      button.append(image);
      button.addEventListener('click', () => pswp.goTo(index));
      this.#thumbs.append(button);
      return button;
    });

    element.append(this.#previousButton, this.#thumbs, this.#nextButton);
    this.#previousButton.addEventListener('click', () => this.#pageRail(-1));
    this.#nextButton.addEventListener('click', () => this.#pageRail(1));
    this.#thumbs.addEventListener('scroll', () => this.#updateRailButtons(), { passive: true });

    const sync = () => {
      this.#syncActiveThumb(pswp.currIndex);
      this.#updateRailButtons();
      if (this.#titleUsed()) this.#presentCaption(pswp.currIndex);
    };
    pswp.on('change', sync);
    pswp.on('resize', () => this.#syncViewport(pswp));
    pswp.on('openingAnimationEnd', () => {
      this.#updateRailButtons();
      if (this.#titleUsed()) this.#scheduleCaptionAnchor();
    });
    if (this.#titleUsed()) {
      pswp.on('loadComplete', () => this.#scheduleCaptionAnchor());
    }
    this.#syncViewport(pswp);
    sync();
  }

  #createRailButton(direction) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `masonry-lightbox__rail-button masonry-lightbox__rail-button--${direction}`;
    button.setAttribute('aria-label', direction === 'previous' ? this.dataset.labelPrevious || '' : this.dataset.labelNext || '');
    const icon = document.getElementById('template-icon-chevron')?.content?.firstElementChild?.cloneNode(true);
    if (icon) {
      if (direction === 'previous') icon.classList.add('icon--left');
      button.append(icon);
    }
    return button;
  }

  #pageRail(direction) {
    if (!this.#thumbs) return;
    this.#thumbs.scrollBy({
      left: direction * this.#thumbs.clientWidth,
      behavior: this.#reducedMotionQuery.matches ? 'auto' : 'smooth',
    });
  }

  #syncActiveThumb(index) {
    if (this.#syncingThumb) return;
    this.#syncingThumb = true;
    try {
      this.#thumbButtons.forEach((button, buttonIndex) => {
        const active = buttonIndex === index;
        button.classList.toggle('is-active', active);
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
        this.#prepareThumb(button.querySelector('img'), buttonIndex, index);
      });

      const activeThumb = this.#thumbButtons[index];
      if (!activeThumb || !this.#thumbs) return;
      const target = activeThumb.offsetLeft - (this.#thumbs.clientWidth - activeThumb.clientWidth) / 2;
      this.#thumbs.scrollTo({
        left: target,
        behavior: this.#reducedMotionQuery.matches ? 'auto' : 'smooth',
      });
    } finally {
      this.#syncingThumb = false;
    }
  }

  #prepareThumb(image, index, activeIndex) {
    if (!image || image.dataset.masonryThumbReady === 'true') return;
    const eager = Math.abs(index - activeIndex) <= 1;
    if (!image.getAttribute('src')) {
      image.loading = eager ? 'eager' : 'lazy';
      image.decoding = 'async';
      const ready = () => {
        image.dataset.masonryThumbReady = 'true';
        image.loading = 'eager';
        image.decoding = 'sync';
      };
      image.addEventListener('load', ready, { once: true });
      image.addEventListener('error', ready, { once: true });
      return;
    }
    if (eager && image.loading !== 'eager') image.loading = 'eager';
  }

  #updateRailButtons() {
    if (!this.#thumbs || !this.#previousButton || !this.#nextButton) return;
    const maxScroll = this.#thumbs.scrollWidth - this.#thumbs.clientWidth;
    const fits = !this.#mobileQuery.matches && maxScroll <= 1;
    this.#thumbs.parentElement?.classList.toggle('is-rail-fit', fits);
    this.#previousButton.disabled = this.#thumbs.scrollLeft <= 1;
    this.#nextButton.disabled = maxScroll <= 1 || this.#thumbs.scrollLeft >= maxScroll - 1;
  }

  #syncViewport(pswp = this.#pswp) {
    if (this.#syncingViewport || !pswp?.element) return;
    this.#syncingViewport = true;
    try {
      const element = pswp.element;
      const mobile = this.#mobileQuery.matches;
      element.style.setProperty('--masonry-lightbox-rail-height', `${this.#railHeight()}px`);
      element.style.setProperty('--masonry-lightbox-thumb-size', `${this.#thumbSize()}px`);
      this.#thumbButtons.forEach((button) => {
        const image = button.querySelector('img');
        const src = mobile ? button.dataset.thumbSrcMobile : button.dataset.thumbSrc;
        if (image && src && image.getAttribute('src') !== src) image.setAttribute('src', src);
      });
      this.#updateRailButtons();
      if (this.#titleUsed()) this.#presentCaption(pswp.currIndex);
    } finally {
      this.#syncingViewport = false;
    }
  }

  #titleActive() {
    return this.#mobileQuery.matches ? this.dataset.titleMobile === 'true' : this.dataset.titleDesktop === 'true';
  }

  #titleUsed() {
    return this.dataset.titleDesktop === 'true' || this.dataset.titleMobile === 'true';
  }

  #presentCaption(index) {
    if (!this.#caption) return;
    if (this.#titleActive()) {
      this.#syncCaption(index);
      return;
    }

    if (this.#captionFrame) cancelAnimationFrame(this.#captionFrame);
    this.#captionFrame = 0;
    this.#caption.hidden = true;
    this.#caption.classList.remove('is-placed');
    this.#captionHeight = 0;
  }

  #captionHtml(trigger) {
    const desktop = trigger?.dataset.imageTitle || '';
    if (!this.#mobileQuery.matches) return desktop;
    const mobile = trigger?.dataset.imageTitleMobile || '';
    const probe = document.createElement('div');
    probe.innerHTML = mobile;
    return probe.textContent.trim() ? mobile : desktop;
  }

  #syncCaption(index) {
    if (!this.#titleActive() || !this.#caption) return;
    this.#caption.hidden = false;
    const trigger = this.querySelectorAll('a.masonry-lightbox__trigger')[index];
    const next = this.#captionHtml(trigger);
    if (this.#caption.innerHTML !== next) this.#caption.innerHTML = next;
    const height = this.#caption.textContent.trim() ? Math.ceil(this.#caption.getBoundingClientRect().height) : 0;
    const heightChanged = height > 0 && height !== this.#captionHeight;
    if (height > 0) this.#captionHeight = height;
    if (heightChanged && this.#pswp?.isOpen && !this.#placingCaption) {
      this.#placingCaption = true;
      this.#pswp.updateSize(true);
      this.#placingCaption = false;
    }
    this.#scheduleCaptionAnchor();
  }

  #scheduleCaptionAnchor() {
    if (!this.#titleActive()) return;
    if (this.#captionFrame) cancelAnimationFrame(this.#captionFrame);
    this.#captionFrame = requestAnimationFrame(() => {
      this.#captionFrame = requestAnimationFrame(() => {
        this.#captionFrame = 0;
        this.#anchorCaption();
      });
    });
  }

  #anchorCaption() {
    const caption = this.#caption;
    if (!this.#titleActive() || !caption) return;
    caption.style.removeProperty('top');
    caption.style.removeProperty('bottom');
    caption.classList.add('is-placed');
  }

  #thumbSize() {
    const value = Number(this.#mobileQuery.matches ? this.dataset.thumbSizeMobile : this.dataset.thumbSize);
    return Number.isFinite(value) && value > 0 ? value : 50;
  }

  #ratioParts() {
    const value = this.dataset.thumbRatio || '1:1';
    if (value === 'natural') return null;
    const [width, height] = value.split(':').map(Number);
    if (!width || !height) return { width: 1, height: 1 };
    return { width, height };
  }

  #thumbAspectRatio(trigger) {
    const ratio = this.#ratioParts();
    if (ratio) return `${ratio.width} / ${ratio.height}`;
    const imageRatio = Number(trigger?.dataset.imageRatio);
    return String(imageRatio > 0 ? imageRatio : 1);
  }

  #thumbBoxHeight(trigger) {
    const width = this.#thumbSize();
    const ratio = this.#ratioParts();
    if (ratio) return Math.max(1, Math.round((width * ratio.height) / ratio.width));
    const imageRatio = Number(trigger?.dataset.imageRatio);
    if (imageRatio > 0) return Math.max(1, Math.round(width / imageRatio));
    return width;
  }

  #maxThumbHeight() {
    const triggers = this.querySelectorAll('a.masonry-lightbox__trigger');
    let max = 0;
    triggers.forEach((trigger) => {
      max = Math.max(max, this.#thumbBoxHeight(trigger));
    });
    return max || this.#thumbSize();
  }

  #topPadding() {
    const bar = this.#pswp?.element?.querySelector('.pswp__top-bar');
    const measured = bar ? parseFloat(getComputedStyle(bar).height) : NaN;
    if (Number.isFinite(measured) && measured > 0) return Math.round(measured);

    const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    return Math.round(Math.min(rootSize * 6, Math.max(rootSize * 4, window.innerWidth * 0.14)));
  }

  #railHeight() {
    return Math.max(this.#maxThumbHeight(), 36) + 28;
  }

  #bottomPadding() {
    if (!this.#titleActive()) return this.#railHeight();
    return this.#railHeight() + Math.max(this.#captionHeight, 44) + 12;
  }

  #triggerImage(trigger) {
    return trigger?.closest('.masonry__feature')?.querySelector('.masonry__media img') || null;
  }
}

function iconMarkup(templateId, extraClass) {
  const icon = document.getElementById(templateId)?.content?.firstElementChild?.cloneNode(true);
  if (!icon) return '';
  icon.classList.add('pswp__icn');
  if (extraClass) icon.classList.add(extraClass);
  return icon.outerHTML;
}

if (!customElements.get('masonry-lightbox')) {
  customElements.define('masonry-lightbox', MasonryLightbox);
}
